import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import Shell from "@/components/Shell";
import CurrentScriptCard, { TINT_BY_TYPE } from "@/components/CurrentScriptCard";
import {
  Sparkles, Upload, Plus, Trash2, Loader2, FileText,
  ChevronDown, ChevronUp, PenSquare, ArrowLeft, Wand2,
} from "lucide-react";

export default function ClientDetailPage() {
  const { clientId } = useParams();
  const nav = useNavigate();
  const [client, setClient] = useState(null);
  const [samples, setSamples] = useState([]);
  const [scripts, setScripts] = useState([]);
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileOpen, setProfileOpen] = useState(false);

  const [pasteText, setPasteText] = useState("");
  const [pasteSource, setPasteSource] = useState("");
  const [addingSample, setAddingSample] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);

  const load = useCallback(async () => {
    try {
      const [c, s, sc] = await Promise.all([
        api.get(`/clients/${clientId}`),
        api.get(`/clients/${clientId}/samples`),
        api.get(`/clients/${clientId}/scripts`),
      ]);
      setClient(c.data);
      setSamples(s.data);
      setScripts(sc.data);
      setCurrent(sc.data[0] || null);
    } catch { toast.error("Failed to load client"); }
    finally { setLoading(false); }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const addSample = async (e) => {
    e.preventDefault();
    if (pasteText.trim().length < 20) { toast.error("Sample must be at least 20 characters"); return; }
    setAddingSample(true);
    try {
      const { data } = await api.post(`/clients/${clientId}/samples`, {
        content: pasteText, source_name: pasteSource || "Pasted text",
      });
      setSamples((s) => [data, ...s]);
      setClient((c) => ({ ...c, sample_count: (c?.sample_count || 0) + 1 }));
      setPasteText(""); setPasteSource("");
      toast.success("Sample added");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to add sample"); }
    finally { setAddingSample(false); }
  };

  const [uploadProgress, setUploadProgress] = useState(null); // { done, total }
  const uploadFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    setUploadProgress({ done: 0, total: files.length });
    const added = [];
    const failed = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post(`/clients/${clientId}/samples/upload`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        added.push(data);
      } catch (err) {
        failed.push({ name: file.name, detail: err?.response?.data?.detail || "upload failed" });
      }
      setUploadProgress({ done: i + 1, total: files.length });
    }
    if (added.length > 0) {
      setSamples((s) => [...added, ...s]);
      setClient((c) => ({ ...c, sample_count: (c?.sample_count || 0) + added.length }));
      toast.success(`Imported ${added.length} of ${files.length} file${files.length !== 1 ? "s" : ""}`);
    }
    for (const f of failed) toast.error(`${f.name}: ${f.detail}`);
    setUploading(false);
    setUploadProgress(null);
    e.target.value = "";
  };

  const removeSample = async (id) => {
    try {
      await api.delete(`/clients/${clientId}/samples/${id}`);
      setSamples((s) => s.filter((x) => x.id !== id));
      setClient((c) => ({ ...c, sample_count: Math.max(0, (c?.sample_count || 1) - 1) }));
    } catch { toast.error("Failed to delete"); }
  };

  const analyze = async () => {
    setAnalyzing(true);
    try {
      const { data } = await api.post(`/clients/${clientId}/analyze`);
      setClient(data);
      setProfileOpen(true);
      toast.success("Voice profile updated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to analyze"); }
    finally { setAnalyzing(false); }
  };

  const deleteScript = async (id) => {
    try {
      await api.delete(`/scripts/${id}`);
      setScripts((s) => s.filter((x) => x.id !== id));
      if (current?.id === id) setCurrent(null);
    } catch { toast.error("Failed to delete"); }
  };

  const updateScriptInList = (updated) => {
    setScripts((arr) => arr.map((x) => (x.id === updated.id ? updated : x)));
    if (current?.id === updated.id) setCurrent(updated);
  };

  if (loading) return (
    <Shell><div className="mono-label pulse-soft">Loading client</div></Shell>
  );
  if (!client) return (
    <Shell>
      <div className="max-w-md">
        <h1 className="font-serif-display text-4xl">Client not found</h1>
        <Link to="/" className="mono-label mt-4 inline-block hover:text-black transition-colors">← Back to dashboard</Link>
      </div>
    </Shell>
  );

  return (
    <Shell>
      <div className="max-w-6xl mx-auto">
        {/* Back + Header */}
        <button
          onClick={() => nav("/")}
          data-testid="back-to-home"
          className="mono-label flex items-center gap-1 mb-6 hover:text-black transition-colors"
        >
          <ArrowLeft className="w-3 h-3" /> Back to dashboard
        </button>

        <div className="flex items-start justify-between gap-6 mb-10 flex-wrap">
          <div className="min-w-0">
            <div className="mono-label mb-2">Client</div>
            <h1 className="font-serif-display text-5xl md:text-6xl tracking-tight leading-none" data-testid="client-name">
              {client.name}
            </h1>
            {client.description && (
              <p className="mt-3 text-base" style={{ color: "var(--ink-2)" }}>{client.description}</p>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              data-testid="analyze-voice-button"
              onClick={analyze}
              disabled={analyzing || samples.length === 0}
              className="btn-ghost hair px-4 py-2.5 rounded-md text-sm inline-flex items-center gap-2"
              style={{ background: "var(--paper)" }}
            >
              {analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {client.voice_profile ? "Re-analyze voice" : "Analyze voice"}
            </button>
            <Link
              to={`/clients/${clientId}/generate`}
              data-testid="new-script-link"
              className="btn-primary text-sm px-4 py-2.5 rounded-md inline-flex items-center gap-2"
            >
              <PenSquare className="w-4 h-4" /> New script
            </Link>
          </div>
        </div>

        {/* Collapsible voice profile */}
        {client.voice_profile && (
          <section className="mb-10 rise-in" data-testid="voice-profile-section">
            <button
              onClick={() => setProfileOpen((v) => !v)}
              data-testid="toggle-voice-profile"
              className="w-full text-left hair rounded-md px-5 py-3 flex items-center justify-between transition-colors duration-200 hover:bg-[var(--surface-hover)]"
              style={{ background: "var(--paper)" }}
              aria-expanded={profileOpen}
            >
              <div className="flex items-center gap-3">
                <span className="dot" style={{ background: "var(--lavender-deep)" }} />
                <span className="mono-label">Voice profile</span>
                <span className="text-xs" style={{ color: "var(--ink-2)" }}>
                  {profileOpen ? "click to hide" : "click to reveal"}
                </span>
              </div>
              {profileOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            {profileOpen && (
              <div className="tint-lavender-soft hair rounded-md p-6 mt-2 whitespace-pre-wrap text-sm leading-relaxed rise-in" data-testid="voice-profile-content">
                {client.voice_profile}
              </div>
            )}
          </section>
        )}

        {/* Current script (if any) */}
        {current && (
          <section className="mb-12">
            <div className="mono-label mb-4">Latest script</div>
            <CurrentScriptCard
              key={current.id}
              script={current}
              onUpdated={updateScriptInList}
              clientName={client.name}
            />
          </section>
        )}

        <div className="grid lg:grid-cols-2 gap-10">
          {/* Samples */}
          <section>
            <div className="flex items-center justify-between mb-4">
              <div className="mono-label">Voice samples · {samples.length}</div>
              <label className="btn-ghost hair text-xs px-3 py-1.5 rounded-md inline-flex items-center gap-2 cursor-pointer" style={{ background: "var(--paper)" }} data-testid="upload-sample-label">
                {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                {uploading && uploadProgress
                  ? `Uploading ${uploadProgress.done} / ${uploadProgress.total}`
                  : "Upload .txt / .pdf / .docx"}
                <input data-testid="upload-sample-input" type="file" multiple accept=".txt,.md,.pdf,.docx" hidden onChange={uploadFile} />
              </label>
            </div>

            <form onSubmit={addSample} className="mb-4 space-y-3">
              <input
                data-testid="paste-source-name"
                placeholder="Source (e.g. LinkedIn post, Aug '25)"
                value={pasteSource} onChange={(e) => setPasteSource(e.target.value)}
                className="w-full surface hair px-3 py-2 rounded-md text-sm focus-primary"
              />
              <textarea
                data-testid="paste-content"
                placeholder="Paste a piece of the client's writing here…"
                rows={4}
                value={pasteText} onChange={(e) => setPasteText(e.target.value)}
                className="w-full surface hair px-3 py-2 rounded-md text-sm focus-primary resize-y"
              />
              <button
                data-testid="add-sample-button"
                type="submit" disabled={addingSample}
                className="btn-primary text-sm px-4 py-2 rounded-md inline-flex items-center gap-2"
              >
                {addingSample ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add sample
              </button>
            </form>

            <ul className="space-y-2" data-testid="samples-list">
              {samples.map((s, i) => (
                <li key={s.id} className="hair rounded-md p-4 flex items-start gap-3 rise-in" style={{ animationDelay: `${i * 30}ms`, background: "var(--paper)" }}>
                  <FileText className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "var(--primary)" }} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-sm font-medium truncate">{s.source_name}</div>
                      <div className="mono-label whitespace-nowrap">{s.word_count} words</div>
                    </div>
                    <p className="text-sm mt-1" style={{ color: "var(--ink-2)" }}>{s.preview}</p>
                  </div>
                  <button
                    data-testid={`delete-sample-${s.id}`}
                    onClick={() => removeSample(s.id)}
                    className="btn-ghost p-1 rounded"
                    aria-label="Delete sample"
                  >
                    <Trash2 className="w-3.5 h-3.5" style={{ color: "var(--ink-2)" }} />
                  </button>
                </li>
              ))}
              {samples.length === 0 && (
                <li className="text-sm py-6" style={{ color: "var(--ink-2)" }}>
                  No samples yet. Paste writing or upload a file to teach us their voice.
                </li>
              )}
            </ul>
          </section>

          {/* Script history */}
          <section>
            <div className="mono-label mb-4">Script history · {scripts.length}</div>
            {scripts.length === 0 ? (
              <div className="hair rounded-md p-8 text-center" style={{ background: "var(--paper)" }}>
                <div className="font-serif-display text-2xl mb-2">No scripts yet</div>
                <p className="text-sm mb-5" style={{ color: "var(--ink-2)" }}>
                  Once you have a sample or two, spin up a script tailored to their voice.
                </p>
                <Link
                  to={`/clients/${clientId}/generate`}
                  data-testid="empty-generate-link"
                  className="btn-primary text-sm px-4 py-2 rounded-md inline-flex items-center gap-2"
                >
                  <PenSquare className="w-4 h-4" /> Generate the first
                </Link>
              </div>
            ) : (
              <ul className="space-y-2" data-testid="scripts-history">
                {scripts.map((s, i) => {
                  const t = TINT_BY_TYPE[s.script_type];
                  return (
                    <li key={s.id} className="rise-in" style={{ animationDelay: `${i * 30}ms` }}>
                      <button
                        data-testid={`script-item-${s.id}`}
                        onClick={() => setCurrent(s)}
                        className="w-full text-left hair rounded-md px-4 py-3 flex items-center justify-between hover:bg-[var(--surface-hover)] transition-colors duration-200"
                        style={{ background: "var(--paper)" }}
                      >
                        <div className="min-w-0 flex items-center gap-3">
                          <span className="dot" style={{ background: t?.dot }} />
                          <div className="min-w-0">
                            <div className="text-sm font-medium truncate">{s.topic}</div>
                            <div className="mono-label mt-1">{s.script_type} · {s.model} · {new Date(s.created_at).toLocaleDateString()}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <span
                            role="button" tabIndex={0}
                            data-testid={`remix-history-${s.id}`}
                            onClick={(e) => { e.stopPropagation(); nav(`/clients/${clientId}/generate?remix=${s.id}`); }}
                            onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); nav(`/clients/${clientId}/generate?remix=${s.id}`); } }}
                            className="btn-ghost p-1.5 rounded inline-flex items-center gap-1 text-xs"
                            title="Remix this brief"
                          >
                            <Wand2 className="w-3.5 h-3.5" style={{ color: "var(--lavender-deep)" }} />
                          </span>
                          <span
                            role="button" tabIndex={0}
                            data-testid={`delete-script-${s.id}`}
                            onClick={(e) => { e.stopPropagation(); deleteScript(s.id); }}
                            onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); deleteScript(s.id); } }}
                            className="btn-ghost p-1.5 rounded"
                          >
                            <Trash2 className="w-3.5 h-3.5" style={{ color: "var(--ink-2)" }} />
                          </span>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </div>
      </div>
    </Shell>
  );
}
